import { UIEvent, useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import styled from 'styled-components';

import { theme } from '@styles/theme';
import { pxToRem } from '@utils/converters';
import { transitionSlowest } from '@utils/transitions';

import {
  useIgnoreFindings,
  useInvalidateJourneyReviews,
  useCancelJourneyReview,
  useLatestJourneyReview,
  useRestoreFindings,
  useStartJourneyReview,
} from 'api/journey-reviews/hooks';
import { ReviewFinding } from 'api/journey-reviews/requests';
import { findingSignature } from 'api/journey-reviews/signature';

import { resolveReviewPanelState } from './resolve-panel-state';
import { ReviewPanelView } from './review-panel-view';
import { useFindingNavigation } from './use-finding-navigation';
import { useJourneyReviewWebSocket } from './use-journey-review-websocket';
import { useReviewPanelStore } from './use-review-panel-store';

export type ReviewPanelProps = {
  journeyShortId: string;
};

const ATTRIBUTES_SECTION = 'attributes';

// No timeout of its own: the task's own time limit is what turns a stuck run into
// a `failed` row, so the panel always shows what actually happened.

// Module-level so the offset survives the panel unmounting on a tab change.
const scrollTopByJourney = new Map<string, number>();

export const ReviewPanel = ({ journeyShortId }: ReviewPanelProps) => {
  const { isOpen, close, setRunWebsocketId, clearRunWebsocketId } = useReviewPanelStore();
  const websocketId = useReviewPanelStore(
    (store) => store.runWebsocketIdByJourney[journeyShortId] ?? null
  );
  const navigate = useNavigate();
  const location = useLocation();
  const bodyRef = useRef<HTMLDivElement>(null);
  const isStartingRef = useRef(false);
  // The review has landed but the loading checklist is still playing out.
  const [isSettling, setIsSettling] = useState(false);
  const wasRunningRef = useRef(false);

  const { data: review, isInitialLoading } = useLatestJourneyReview(journeyShortId, {
    enabled: isOpen,
  });

  // A run in flight IS an in_progress review, so this survives the panel being
  // closed, unmounted, or started elsewhere.
  const isRunning = review?.status === 'in_progress';
  const startReview = useStartJourneyReview(journeyShortId);
  const cancelReview = useCancelJourneyReview(journeyShortId);
  const { invalidateLatestJourneyReview } = useInvalidateJourneyReviews();
  const ignoreFindings = useIgnoreFindings(journeyShortId);
  const restoreFindings = useRestoreFindings(journeyShortId);
  const jumpToFinding = useFindingNavigation();

  // A list either way, so ignoring a whole severity group is one request and one
  // cache invalidation rather than one of each per finding.
  const handleIgnore = useCallback(
    (findings: ReviewFinding[]) => ignoreFindings.mutate(findings.map(findingSignature)),
    [ignoreFindings]
  );
  const handleRestore = useCallback(
    (findings: ReviewFinding[]) => restoreFindings.mutate(findings.map(findingSignature)),
    [restoreFindings]
  );

  // The socket only pulls the result forward; the poll guarantees it arrives.
  const handleComplete = useCallback(() => {
    invalidateLatestJourneyReview(journeyShortId);
  }, [invalidateLatestJourneyReview, journeyShortId]);

  useJourneyReviewWebSocket({
    websocketId,
    isEnabled: Boolean(isRunning && websocketId),
    onComplete: handleComplete,
  });

  // Play the checklist out only if this panel watched the run; reopening after
  // it finished goes straight to the result.
  useEffect(() => {
    if (isRunning) {
      wasRunningRef.current = true;
      return;
    }
    if (wasRunningRef.current) {
      wasRunningRef.current = false;
      setIsSettling(true);
      clearRunWebsocketId(journeyShortId);
    }
  }, [isRunning, journeyShortId, clearRunWebsocketId]);

  const handleLoadingFinished = useCallback(() => setIsSettling(false), []);

  const handleStopReview = useCallback(() => {
    // Cancelling deletes the run, so forget we saw it running — otherwise the
    // checklist plays out over a review the user just stopped.
    cancelReview.mutate(undefined, {
      onSuccess: () => {
        wasRunningRef.current = false;
        setIsSettling(false);
        clearRunWebsocketId(journeyShortId);
      },
    });
  }, [cancelReview, clearRunWebsocketId, journeyShortId]);

  const handleRunReview = useCallback(() => {
    // Two clicks in one tick see the same state, so only a ref settles it; a
    // genuine concurrent run is rejected server-side with a 409.
    if (isStartingRef.current) return;
    isStartingRef.current = true;

    startReview.mutate(undefined, {
      onSuccess: (started) => setRunWebsocketId(journeyShortId, started.websocketId),
      onSettled: () => {
        isStartingRef.current = false;
      },
    });
  }, [startReview, setRunWebsocketId, journeyShortId]);

  const handleJumpTo = useCallback(
    (finding: ReviewFinding) => {
      if (finding.target.kind === 'field') {
        const segments = location.pathname.replace(/\/$/, '').split('/');
        segments[segments.length - 1] = ATTRIBUTES_SECTION;
        const attributesPath = segments.join('/');
        navigate(
          finding.target.id ? `${attributesPath}?highlight=${finding.target.id}` : attributesPath
        );
        return;
      }
      jumpToFinding(finding.target);
    },
    [jumpToFinding, navigate, location.pathname]
  );

  const handleBodyScroll = useCallback(
    (event: UIEvent<HTMLDivElement>) => {
      scrollTopByJourney.set(journeyShortId, event.currentTarget.scrollTop);
    },
    [journeyShortId]
  );

  const state = resolveReviewPanelState({
    isRunning: Boolean(isRunning),
    isSettling,
    review: review ?? null,
    isInitialLoading,
  });

  // On results appearing, not on every content change, so ignoring a finding
  // doesn't jump you back to the top.
  useLayoutEffect(() => {
    if (isOpen && state === 'results' && bodyRef.current) {
      bodyRef.current.scrollTop = scrollTopByJourney.get(journeyShortId) ?? 0;
    }
  }, [isOpen, state, journeyShortId]);

  return (
    <Panel $isOpen={isOpen} aria-hidden={!isOpen}>
      {isOpen && (
        <ReviewPanelView
          state={state}
          review={review ?? null}
          isFinishing={!isRunning && isSettling}
          onLoadingFinished={handleLoadingFinished}
          onStopReview={handleStopReview}
          isStoppingReview={cancelReview.isLoading}
          onRunReview={handleRunReview}
          onClose={close}
          onJumpTo={handleJumpTo}
          onIgnore={handleIgnore}
          onRestore={handleRestore}
          bodyRef={bodyRef}
          onBodyScroll={handleBodyScroll}
        />
      )}
    </Panel>
  );
};

const Panel = styled.div<{ $isOpen: boolean }>((props) => ({
  position: 'absolute',
  top: 0,
  right: 0,
  bottom: 0,
  width: pxToRem(440),
  display: 'flex',
  flexDirection: 'column',
  background: theme.color.backgroundLightDark,
  borderLeft: `1px solid ${theme.color.border10}`,
  boxShadow: theme.shadow.fancyRight,
  transform: props.$isOpen ? 'translateX(0)' : 'translateX(100%)',
  transition: transitionSlowest('transform'),
  zIndex: 5,
}));
